import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message/description */
  message: string;
  /** Label for the confirm button */
  confirmLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Visual style variant */
  variant?: 'danger' | 'default';
  /** Whether an async operation is in progress (disables buttons and prevents closing) */
  loading?: boolean;
  /** Called when user confirms */
  onConfirm: () => void;
  /** Called when user cancels (including Escape key and backdrop click) */
  onCancel: () => void;
}

/**
 * A reusable confirmation dialog component.
 *
 * Built on top of shadcn/ui AlertDialog with:
 * - Modal overlay with backdrop
 * - Keyboard support (Escape to cancel)
 * - Focus management
 * - Danger/default variants for styling
 * - Loading state that disables buttons and prevents closing
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={showDialog}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   variant="danger"
 *   loading={isDeleting}
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowDialog(false)}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Workaround: Radix DismissableLayer uses a module-level variable to
  // save/restore body.style.pointerEvents. When a dialog opens from within a
  // DropdownMenu (two DismissableLayers overlap in the same commit), the
  // restore value gets clobbered, leaving pointer-events stuck as "none".
  // Defensively reset it after the dialog closes.
  useEffect(() => {
    if (open) return;
    const id = requestAnimationFrame(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Prevent closing the dialog during loading (ESC key, backdrop click, etc.)
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !loading) {
      onCancel();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={variant === 'danger' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={loading}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
